import React from "react";
import { Column, Table, compareNumber } from "../components/table/Table";
import { Link } from "@material-ui/core";
import WarningIcon from '@material-ui/icons/Warning';

/**
 * Colonne d'affichage du nom du véhicule
 */
const vehicleNameColumn: Column<CRM.Bus> = {
  key: 'vehicle',
  label: 'Nom',
  order: 2,
  xs: 2,
  sortable: true,
  compare: (a, b) => (a.name || '').localeCompare(b.name),
  renderCell: value => (
    <span>
      {value.name}
    </span>
  ),
};

/**
 * Colonne d'affichage de l'immatriculation du véhicule
 */
const vehicleRegistrationColumn: Column<CRM.Bus> = {
  key: 'registration',
  label: 'Immat.',
  order: 4,
  xs: 1,
  sortable: true,
  compare: (a, b) => (a.registration || '').localeCompare(b.registration),
  renderCell: value => (
    <span>
      {value.registration}
    </span>
  ),
};

/**
 * Colonne d'affichage du statut fonctionnel du véhicule
 */
const vehicleFunctionalColumn: Column<CRM.Bus> = {
  key: 'functional',
  label: 'Fonctionnel',
  order: 5,
  xs: 1,
  sortable: true,
  compare: (a, b) => (String(a.functionnal) || '').localeCompare(String(b.functionnal)),
  renderCell: value => (
    <span>
      {value.functionnal && value.functionnal === true ? 'Oui' : 'Non'}
    </span>
  ),
};

/**
 * Colonne d'affichage de la capacité du véhicule avec gestion des remplacements
 * 
 * Cette colonne affiche :
 * - La capacité maximale du véhicule
 * - Les places restantes si spécifiées
 * - Des avertissements si des passagers doivent être répartis
 * - Des icônes d'alerte pour les situations problématiques
 * 
 * @param gridType Type de grille (admin, add, replace)
 * @param busToReplacePassengers Liste des nombres de passagers à répartir
 * @param placesRemaining Nombre de places restantes (optionnel)
 * @returns Colonne de capacité du véhicule
 */
function vehicleCapacityColumn(gridType: CRM.VehicleGridType, busToReplacePassengers: number[], placesRemaining?: number | undefined): Column<CRM.Bus> {
  return {
    key: 'capacity',
    label: 'Capacité max',
    order: 3,
    xs: 1,
    sortable: true,
    compare: (a, b) =>
      compareNumber(a.passengerCapacity, b.passengerCapacity),
    renderCell: value => {
      let capacityRemaining: number = value.passengerCapacity;
      let passengersToAllocate: number = 0;
       for(const count of busToReplacePassengers){
        if(capacityRemaining >= count) {
          capacityRemaining = capacityRemaining - count;
        } else {
          passengersToAllocate = passengersToAllocate + count;
        }
      }
      return (
      <div >
        {
        gridType === 'replace' && passengersToAllocate > 0 ?
        (<span title={(passengersToAllocate)+' personnes à répartir dans les autres bus'}
        >{value.passengerCapacity} <WarningIcon style={{verticalAlign:'middle', color:'orange'}}/>
        ({(passengersToAllocate)})
        </span>) : 
        (<span>
          {placesRemaining ? placesRemaining+' pl. restantes' : value.passengerCapacity} 
        </span>)
        }
      </div>
    )},
  }
};

/**
 * Colonne d'action pour modifier/configurer un véhicule
 * 
 * @param onConfigure Fonction appelée lors du clic sur "Configurer"
 * @returns Colonne de modification du véhicule
 */
function vehicleEditColumn(onConfigure: (bus: CRM.BusDocument) => void): Column<CRM.Bus> {
  return {
    key: 'edit',
    label: 'Modifier',
    order: 6,
    xs: 1,
    sortable: false,
    renderCell: value => (
      <span>
        <Link color="secondary" onClick={()=>{onConfigure(value)}} href="#">Configurer</Link>
      </span>
    ),
  }
};

/**
 * Colonne d'affichage des autres sites sur le même créneau horaire
 * 
 * Cette colonne affiche :
 * - Les noms des sites déjà assignés sur ce créneau
 * - Une icône d'avertissement si des conflits existent
 * - La liste des sites séparés par des virgules
 * 
 * @param otherSpotsNames Liste des noms des autres sites
 * @returns Colonne des autres sites sur le créneau
 */
function vehicleOtherSpotsOnTimingColumn(otherSpotsNames: string []): Column<CRM.Bus> {
  return {
    key: 'otherSpots',
    label: 'Spot(s) déjà sur ce créneau',
    order: 6,
    xs: 2,
    sortable: false,
    renderCell: () => (
      <div>
        {otherSpotsNames.length > 0 && (<span><WarningIcon style={{verticalAlign:'middle', color:'orange'}}/>&nbsp;</span>)}
        <span>{otherSpotsNames.join()}</span>
      </div>
    ),
  }
};

/**
 * Colonne d'action pour assigner un véhicule
 * 
 * @param onAssign Fonction appelée lors du clic sur "Attribuer"
 * @returns Colonne d'assignation du véhicule
 */
function vehicleAssignColumn(onAssign: (busId: string) => void): Column<CRM.Bus> {
  return {
    key: 'assign',
    label: 'Attribuer',
    order: 7,
    xs: 1,
    sortable: false,
    renderCell: value => (
      <span>
        <Link color="secondary" onClick={()=>{onAssign(value._id)}} href="#">Attribuer</Link>
      </span>
    ),
  }
};

/**
 * Colonne d'action pour sélectionner un véhicule (ajout ou remplacement)
 * 
 * Cette colonne affiche :
 * - Un lien "Ajouter ce bus" pour le mode "add"
 * - Un lien "Remplacer par ce bus" pour le mode "replace"
 * - L'action appropriée selon le contexte
 * 
 * @param onSelect Fonction appelée lors de la sélection
 * @param gridType Type de grille (add ou replace)
 * @param busTravelId ID du voyage de bus concerné
 * @returns Colonne de sélection du véhicule
 */
function vehicleTravelSelectColumn(onSelect: (busTravelId: string) => void, gridType: CRM.VehicleGridType, busTravelId: string): Column<CRM.Bus> {
  return {
    key: 'select',
    label: gridType === 'add' ? 'Ajouter' : 'Remplacer',
    order: 7,
    xs: 2,
    sortable: false,
    renderCell: () => (
      <span>
        <Link color="secondary" onClick={()=>{onSelect(busTravelId)}} href="#">
        {gridType === 'add' ? 'Ajouter ce bus' : 'Remplacer par ce bus'}
        </Link>
      </span>
    ),
  }
};

/**
 * Type pour le provider de tableau des véhicules
 */
type VehiclesTableProvider = (
  gridType: CRM.VehicleGridType,
  onAssign: (id: string)=> void,
  onConfigure?: (bus: CRM.BusDocument)=> void,
  busToReplacePassengers?: number[],
  busTravelId?: string,
  otherSpotsNamesOnTiming?: string[],
  placesRemaining?: number
) => Table<CRM.Bus>;

/**
 * Provider de colonnes pour le tableau des véhicules
 * 
 * Ce provider génère les colonnes appropriées selon le type de grille :
 * - admin : Colonnes complètes avec actions de configuration et assignation
 * - add : Colonnes pour ajouter un véhicule à un voyage
 * - replace : Colonnes pour remplacer un véhicule dans un voyage
 * 
 * Logique métier :
 * - Colonnes adaptées selon le contexte d'utilisation
 * - Gestion des capacités et conflits de créneaux
 * - Actions contextuelles selon le type de grille
 * - Affichage des avertissements et informations pertinentes
 * 
 * @param gridType Type de grille (admin, add, replace)
 * @param onAssign Fonction d'assignation
 * @param onConfigure Fonction de configuration (optionnelle)
 * @param busToReplacePassengers Passagers à répartir (optionnel)
 * @param busTravelId ID du voyage de bus (optionnel)
 * @param otherSpotsNamesOnTiming Autres sites sur le créneau (optionnel)
 * @param placesRemaining Places restantes (optionnel)
 * @returns Tableau avec colonnes configurées
 */
export const vehiclesTableProvider: VehiclesTableProvider = 
(
  gridType,
  onAssign: (id: string)=> void,
  onConfigure?: (bus: CRM.BusDocument)=> void,
  busToReplacePassengers?,
  busTravelId?,
  otherSpotsNamesOnTiming?,
  placesRemaining?
) => {
  let columns: Column<CRM.Bus>[] = [];

  switch(gridType) {
    case 'admin': {
      columns = [
        vehicleNameColumn,
        vehicleCapacityColumn(gridType, busToReplacePassengers || [], placesRemaining),
        vehicleRegistrationColumn,
        vehicleFunctionalColumn,
        vehicleAssignColumn(onAssign)
      ];
      if(onConfigure) {
        columns.push(vehicleEditColumn(onConfigure));
      }
      break;
    }
    case 'add': {
      columns = [
        vehicleNameColumn,
        vehicleCapacityColumn(gridType, busToReplacePassengers || [], placesRemaining),
        vehicleOtherSpotsOnTimingColumn(otherSpotsNamesOnTiming || []),
        vehicleTravelSelectColumn(onAssign, 'add', busTravelId || '')
      ];
      break;
    }
    case 'replace': {
      columns = [
        vehicleNameColumn,
        vehicleCapacityColumn(gridType, busToReplacePassengers || [], placesRemaining),
        vehicleOtherSpotsOnTimingColumn(otherSpotsNamesOnTiming || []),
        vehicleTravelSelectColumn(onAssign, 'replace', busTravelId || '')
      ];
      break;
    }
  }
  return {
    columns
  };
}